import {
  AccountTree as AccountTreeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  FormatListBulleted as FormatListBulletedIcon,
  OpenInNew as OpenInNewIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Chip,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useApiQuery } from "../../hooks/use-api-query.ts";
import { STATUS_COLORS } from "../../theme/theme";
import type { CheckRun, CheckSchedule, CheckScheduleRun } from "../../types.ts";
import { RunStatusChip } from "../run-status-chip.tsx";
import CheckDependencyGraph from "./check-dependency-graph.tsx";

interface CheckScheduleRunDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  scheduleRun: CheckScheduleRun;
  scheduleId: string;
  // Schedule dependency graph, passed from the parent to avoid a refetch.
  // When absent, the drawer fetches the schedule to obtain it.
  checkDependencyGraph?: Record<string, string[]> | null;
}

// Full schedule run with check runs
interface FullScheduleRun extends CheckScheduleRun {
  check_runs?: CheckRun[];
}

export default function CheckScheduleRunDetailsDrawer({
  open,
  onClose,
  scheduleRun,
  scheduleId,
  checkDependencyGraph,
}: CheckScheduleRunDetailsDrawerProps) {
  const nav = useNavigate();
  const [tab, setTab] = useState<"overview" | "checks">("overview");
  // Check Results: graph (default) vs. flat list.
  const [checksView, setChecksView] = useState<"graph" | "list">("graph");

  // Fetch full run details with check runs
  const { data: fullRun } = useApiQuery<FullScheduleRun>(
    `/check-schedules/${scheduleId}/runs/${scheduleRun.id}`,
    {
      enabled: open && !!scheduleRun.id,
    },
  );

  // Fall back to fetching the schedule when no dependency graph was passed in.
  const { data: fetchedSchedule } = useApiQuery<CheckSchedule>(`/check-schedules/${scheduleId}`, {
    enabled: open && checkDependencyGraph === undefined,
  });

  const run = fullRun || scheduleRun;
  const checkRuns = fullRun?.check_runs ?? [];

  const dependencyGraph =
    checkDependencyGraph !== undefined
      ? checkDependencyGraph
      : (fetchedSchedule?.check_dependency_graph ?? null);

  // Unique checks in this run (by check_id), ordered by start time as a fallback.
  const graphChecks = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; sortKey: number }>();
    for (const cr of checkRuns) {
      if (seen.has(cr.check_id)) continue;
      seen.set(cr.check_id, {
        id: cr.check_id,
        name: cr.check_name || `Check ${cr.check_id.slice(0, 8)}`,
        sortKey: cr.start_time ? new Date(cr.start_time).getTime() : Number.MAX_SAFE_INTEGER,
      });
    }
    return [...seen.values()].sort((a, b) => a.sortKey - b.sortKey);
  }, [checkRuns]);

  // Latest run status per check id, for coloring graph nodes.
  const statusByCheckId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cr of checkRuns) {
      map[cr.check_id] = cr.run_status;
    }
    return map;
  }, [checkRuns]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue as "overview" | "checks");
  };

  const getCheckStatusIcon = (status: string) => {
    switch (status) {
      case "passed":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "failed":
      case "error":
        return <ErrorIcon sx={{ color: "error.dark" }} />;
      default:
        return <RadioButtonUncheckedIcon sx={{ color: "grey.500" }} />;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 700, md: 900, lg: 1000 },
          p: 0,
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Schedule Run Details
            </Typography>
            <RunStatusChip status={run.run_status} />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleTabChange}>
              <Tab label="Overview" value="overview" />
              <Tab label={`Check Results (${checkRuns.length})`} value="checks" />
            </TabList>
          </Box>

          {/* Overview Tab */}
          <TabPanel value="overview" sx={{ flex: 1, overflow: "auto" }}>
            <Stack spacing={3}>
              {/* Run Summary */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Run Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      <RunStatusChip status={run.run_status} />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.duration_seconds
                        ? run.duration_seconds < 60
                          ? `${run.duration_seconds}s`
                          : `${Math.floor(run.duration_seconds / 60)}m ${run.duration_seconds % 60}s`
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Trigger
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.trigger}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Check Results Summary */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Check Results
                </Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {run.total_checks}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Passed
                    </Typography>
                    <Typography variant="h4" fontWeight={600} sx={{ color: "success.main" }}>
                      {run.passed_checks}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      sx={{
                        color: run.failed_checks > 0 ? "error.dark" : "inherit",
                      }}
                    >
                      {run.failed_checks}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Timing */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Timing
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Started At
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.start_time ? new Date(run.start_time).toLocaleString() : "-"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ended At
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {run.end_time ? new Date(run.end_time).toLocaleString() : "-"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {new Date(run.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </TabPanel>

          {/* Checks Tab */}
          <TabPanel value="checks" sx={{ flex: 1, overflow: "auto", p: 0 }}>
            {checkRuns.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No check results available yet.</Typography>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={checksView}
                    onChange={(_, v) => v && setChecksView(v)}
                    aria-label="Check results view mode"
                  >
                    <ToggleButton value="graph" aria-label="Graph view">
                      <AccountTreeIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Graph
                    </ToggleButton>
                    <ToggleButton value="list" aria-label="List view">
                      <FormatListBulletedIcon fontSize="small" sx={{ mr: 0.5 }} />
                      List
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {checksView === "graph" ? (
                  <Box sx={{ p: 2 }}>
                    <CheckDependencyGraph
                      dependencyGraph={dependencyGraph}
                      checks={graphChecks.map((c) => ({ id: c.id, name: c.name }))}
                      statusByCheckId={statusByCheckId}
                    />
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {checkRuns.map((checkRun) => (
                      <ListItem
                        key={checkRun.id}
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          py: 2,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "grey.50",
                          },
                        }}
                        onClick={() => {
                          onClose();
                          nav(`/checks/${checkRun.check_id}?tab=runs&runId=${checkRun.id}`);
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getCheckStatusIcon(checkRun.run_status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography variant="body1" fontWeight={500}>
                                {checkRun.check_name || `Check ${checkRun.check_id.slice(0, 8)}...`}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <RunStatusChip status={checkRun.run_status} />
                                {checkRun.validation_passed !== null && (
                                  <Chip
                                    label={
                                      checkRun.validation_passed
                                        ? "Validation Passed"
                                        : "Validation Failed"
                                    }
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.7rem",
                                      backgroundColor: checkRun.validation_passed
                                        ? STATUS_COLORS.success.bg
                                        : STATUS_COLORS.error.bg,
                                      color: checkRun.validation_passed
                                        ? STATUS_COLORS.success.text
                                        : STATUS_COLORS.error.text,
                                    }}
                                  />
                                )}
                              </Stack>
                            </Stack>
                          }
                          secondary={
                            <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                Duration: {checkRun.duration_ms || "-"}ms
                              </Typography>
                              {checkRun.retry_count > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Retries: {checkRun.retry_count}
                                </Typography>
                              )}
                              {checkRun.error_message && (
                                <Typography variant="caption" color="error">
                                  Error: {checkRun.error_message}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                        <OpenInNewIcon sx={{ color: "grey.500", fontSize: 18, ml: 1 }} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </TabPanel>
        </TabContext>
      </Box>
    </Drawer>
  );
}
